import React, { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { usePlatformAdmin } from '../hooks';
import styles from './Sidebar.module.css';

interface SidebarProps {
  onLogout: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  onLogout
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const { isPlatformAdmin } = usePlatformAdmin();

  return (
    <aside className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''}`}>
      <div className={styles.header}>
        <div className={styles.logo}>Interlucent</div>
        <button
          className={styles.collapseToggle}
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M10 4L6 8L10 12" strokeLinecap="square" />
          </svg>
        </button>
      </div>

      <nav className={styles.nav}>
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Navigate</div>
          <ul className={styles.navItems}>
            <li className={styles.navItem}>
              <Link
                to="/projects"
                className={styles.navLink}
                activeProps={{ className: `${styles.navLink} ${styles.active}` }}
              >
                <div className={styles.navIcon}>
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="3" width="6" height="6" rx="1" />
                    <rect x="11" y="3" width="6" height="6" rx="1" />
                    <rect x="3" y="11" width="6" height="6" rx="1" />
                    <rect x="11" y="11" width="6" height="6" rx="1" />
                  </svg>
                </div>
                <span className={styles.navText}>Projects</span>
              </Link>
            </li>

            <li className={styles.navItem}>
              <Link
                to="/analytics"
                className={styles.navLink}
                activeProps={{ className: `${styles.navLink} ${styles.active}` }}
              >
                <div className={styles.navIcon}>
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M3 17V12M10 17V7M17 17V3" strokeLinecap="square" />
                  </svg>
                </div>
                <span className={styles.navText}>Analytics</span>
              </Link>
            </li>

            <li className={styles.navItem}>
              <Link
                to="/documentation"
                className={styles.navLink}
                activeProps={{ className: `${styles.navLink} ${styles.active}` }}
              >
                <div className={styles.navIcon}>
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M5 3H13L15 5V17H5V3Z" />
                    <path d="M13 3V5H15" />
                    <path d="M7 9H13M7 12H11" strokeLinecap="square" />
                  </svg>
                </div>
                <span className={styles.navText}>Develop</span>
              </Link>
            </li>

            {isPlatformAdmin && (
              <>
                <li className={styles.navItem}>
                  <Link
                    to="/admin/pools"
                    className={styles.navLink}
                    activeProps={{ className: `${styles.navLink} ${styles.active}` }}
                  >
                    <div className={styles.navIcon}>
                      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M10 2L3 6V14L10 18L17 14V6L10 2Z" />
                        <path d="M10 8V12M10 14V14.01" strokeLinecap="square" />
                      </svg>
                    </div>
                    <span className={styles.navText}>Pools</span>
                  </Link>
                </li>
                <li className={styles.navItem}>
                  <Link
                    to="/admin/organizations"
                    className={styles.navLink}
                    activeProps={{ className: `${styles.navLink} ${styles.active}` }}
                  >
                    <div className={styles.navIcon}>
                      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M17 17V15C17 13.3431 15.6569 12 14 12H6C4.34315 12 3 13.3431 3 15V17" />
                        <circle cx="10" cy="7" r="3" />
                      </svg>
                    </div>
                    <span className={styles.navText}>Organizations</span>
                  </Link>
                </li>
                <li className={styles.navItem}>
                  <Link
                    to="/admin/plan-tiers"
                    className={styles.navLink}
                    activeProps={{ className: `${styles.navLink} ${styles.active}` }}
                  >
                    <div className={styles.navIcon}>
                      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M3 4H17M3 4V7L7 10L3 13V16H17V13L13 10L17 7V4" />
                      </svg>
                    </div>
                    <span className={styles.navText}>Plan Tiers</span>
                  </Link>
                </li>
                <li className={styles.navItem}>
                  <Link
                    to="/admin/reports"
                    className={styles.navLink}
                    activeProps={{ className: `${styles.navLink} ${styles.active}` }}
                  >
                    <div className={styles.navIcon}>
                      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M3 16V7L7 4L11 7L15 3L17 5V16H3Z" />
                        <path d="M3 16H17" />
                      </svg>
                    </div>
                    <span className={styles.navText}>Reports</span>
                  </Link>
                </li>
                <li className={styles.navItem}>
                  <Link
                    to="/admin/tools"
                    className={styles.navLink}
                    activeProps={{ className: `${styles.navLink} ${styles.active}` }}
                  >
                    <div className={styles.navIcon}>
                      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M13 3L17 7L8 16H4V12L13 3Z" strokeLinecap="square" strokeLinejoin="miter" />
                        <path d="M11 5L15 9" strokeLinecap="square" />
                      </svg>
                    </div>
                    <span className={styles.navText}>Tools</span>
                  </Link>
                </li>
              </>
            )}
          </ul>
        </div>
      </nav>

      <div className={styles.footer}>
        <Link to="/settings" className={styles.footerItem}>
          <div className={styles.footerIcon}>
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="10" cy="10" r="3" />
              <path d="M10 3V4M10 16V17M17 10H16M4 10H3M14.5 14.5L13.8 13.8M6.2 6.2L5.5 5.5M14.5 5.5L13.8 6.2M6.2 13.8L5.5 14.5" strokeLinecap="square" />
            </svg>
          </div>
          <span className={styles.footerText}>Settings</span>
        </Link>

        <div className={styles.footerItem} onClick={onLogout}>
          <div className={styles.footerIcon}>
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M13 3H16C16.5523 3 17 3.44772 17 4V16C17 16.5523 16.5523 17 16 17H13M9 14L13 10L9 6M13 10H3" strokeLinecap="square" />
            </svg>
          </div>
          <span className={styles.footerText}>Logout</span>
        </div>
      </div>
    </aside>
  );
};
